/**
 * useSocialDrawerVisibility.ts
 *
 * Hook for fetching social drawer feature visibility settings from the backend.
 * Returns visibility settings to determine which tabs should be shown/hidden
 * in the social drawer, along with helper functions to check feature status.
 *
 * Cache Strategy:
 * - staleTime: 5 minutes - Visibility settings rarely change, so we don't need
 *   to refetch on every mount. This prevents unnecessary API calls while
 *   still keeping data reasonably fresh.
 * - gcTime: 30 minutes - Keep cached data longer for background tab recovery,
 *   reducing API load when users navigate back to pages with the social drawer.
 */
import {
    SocialDrawerApi,
    SocialDrawerFeature,
} from "@devour/client";
import { useQuery } from "@tanstack/react-query";
import getConfig from "@/utils/getConfig";
export { SocialDrawerFeature };

/**
 * Visibility response from API
 */
export interface SocialDrawerVisibilityResponse {
    id: string;
    feature: SocialDrawerFeature;
    isEnabled: boolean;
    disabledMessage: string;
    displayOrder: number;
    displayLabel: string;
}

export const SOCIAL_DRAWER_VISIBILITY_QUERY_KEY = "social-drawer-visibility";

/**
 * Hook to fetch social drawer visibility settings
 *
 * @returns Visibility settings for each social drawer feature
 *
 * @example
 * ```tsx
 * const { visibility, isLoading, isFeatureEnabled, enabledFeatures } = useSocialDrawerVisibility();
 *
 * // Check if a specific feature is enabled
 * if (isFeatureEnabled(SocialDrawerFeature.CLIPS)) {
 *   // Show clips tab
 * }
 *
 * // Get all enabled features
 * enabledFeatures.forEach(feature => {
 *   // Render enabled features only
 * });
 * ```
 */
export function useSocialDrawerVisibility() {
    const query = useQuery({
        queryKey: [SOCIAL_DRAWER_VISIBILITY_QUERY_KEY],
        queryFn: async (): Promise<SocialDrawerVisibilityResponse[]> => {
            const data = await new SocialDrawerApi(getConfig(null)).getSocialDrawerVisibility();
            return ((data as any).visibility || []) as SocialDrawerVisibilityResponse[];
        },
        staleTime: 60 * 1000, // 1 minute
        gcTime: 30 * 60 * 1000, // 30 minutes
    });

    const visibility = query.data || [];

    /**
     * Check if a specific feature is enabled
     */
    function isFeatureEnabled(feature: SocialDrawerFeature): boolean {
        const setting = visibility.find(v => v.feature === feature);
        // Default to true if setting not found
        return setting?.isEnabled ?? true;
    }

    /**
     * Get the disabled message for a feature
     */
    function getDisabledMessage(feature: SocialDrawerFeature): string {
        const setting = visibility.find(v => v.feature === feature);
        return setting?.disabledMessage || "Coming Soon";
    }

    /**
     * Get all enabled features sorted by displayOrder
     */
    const enabledFeatures = visibility
        .filter(v => v.isEnabled)
        .sort((a, b) => a.displayOrder - b.displayOrder);

    /**
     * Check if any feature is enabled (for hiding entire drawer)
     * Default to true while loading to prevent flash of hidden content
     */
    const hasAnyEnabledFeature = query.isLoading || query.isError
        ? true // Default to showing while loading or on error
        : enabledFeatures.length > 0;

    return {
        visibility,
        isLoading: query.isLoading,
        isError: query.isError,
        isFeatureEnabled,
        getDisabledMessage,
        enabledFeatures,
        hasAnyEnabledFeature,
    };
}
